import apiClient from './api';
import type {
  PushToken,
  CategoryPreferencesData,
  CategoryPreferenceUpdate,
  TestSendInput,
  TestSendResult,
} from '@/types/push';

const BASE = '/api/v1/notifications/push';

/**
 * VIS-188 — Push infrastructure client. All requests go through the API
 * Gateway (never a service port directly).
 */
export const pushService = {
  /** Register (or refresh) a device push token. */
  async registerToken(input: { token: string; platform?: string; deviceName?: string }): Promise<PushToken> {
    const response = await apiClient.post<PushToken>(`${BASE}/tokens`, input);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to register push token');
    }
    return response.data;
  },

  /** List the current user's registered device tokens. */
  async listTokens(): Promise<PushToken[]> {
    const response = await apiClient.get<{ tokens: PushToken[] }>(`${BASE}/tokens`);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to list push tokens');
    }
    return response.data.tokens;
  },

  /** Unregister a device token. */
  async unregisterToken(token: string): Promise<void> {
    const response = await apiClient.delete(`${BASE}/tokens/${encodeURIComponent(token)}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to unregister push token');
    }
  },

  /** Get the effective per-category push preferences (balanced default filled in). */
  async getCategoryPreferences(): Promise<CategoryPreferencesData> {
    const response = await apiClient.get<CategoryPreferencesData>(`${BASE}/category-preferences`);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to load category preferences');
    }
    return response.data;
  },

  /** Persist per-category push preference tuning. */
  async updateCategoryPreferences(preferences: CategoryPreferenceUpdate[]): Promise<CategoryPreferencesData> {
    const response = await apiClient.put<CategoryPreferencesData>(`${BASE}/category-preferences`, { preferences });
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to save category preferences');
    }
    return response.data;
  },

  /** Fire a test push; on web this drives the in-app notification + tap-through. */
  async testSend(input: TestSendInput): Promise<TestSendResult> {
    const response = await apiClient.post<TestSendResult>(`${BASE}/test-send`, input);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to send test push');
    }
    return response.data;
  },
};
